import React, { useMemo } from 'react';
import clsx from 'clsx';
import { cva } from 'class-variance-authority';
import { motion, AnimatePresence } from 'motion/react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/use-auth-store';
import { useShallow } from 'zustand/shallow';
import { useNavigateToWorkspaceView } from '@/hooks/navigation';
import { shortcutLabel } from '@/lib/platform/platform';
import { useSessionState } from '@/session';
import { IconButton, Flex, Text, Avatar, Dropdown, Tooltip, Menu, Button, Icon, Collapse, useBreakpoint, useReducedMotion } from '@nula/core-ui';
import { useSyncStatus, type SyncStatus } from '@/lib/sync';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useConversationList } from '@/features/assistant/hooks/use-conversation-list';
import { selectRecentEntities } from '@/utils/select-recent-entities';
import { RecentsRow } from '@/layout/recents-row';
import { WindowControls } from '@/layout/window-controls';
import { SidebarBrand } from '@/layout/sidebar-brand';
import { useWorkspaceStore } from '@/stores/use-workspace-store';
import { workspaceInitials, workspaceAvatarColor } from '@/lib/workspace/utils/workspace-avatar';
import { CreateWorkspaceModal } from '@/features/workspaces/components/create-workspace-modal';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import { PlanIndicator } from '@/lib/billing/plan-indicator';
import { subscriptionTabForWorkspace } from '@/lib/billing/subscription-tab-for-workspace';
import { openMessenger } from '@/lib/support/intercom';
import styles from './sidebar.module.css';

const nav = cva([styles.nav], {
  variants: {
    collapsed: { true: styles.navCollapsed, false: styles.navExpanded },
    smooth: { true: styles.navSmooth, false: '' },
  },
});
const profileSection = cva(styles.profileSection);

// ── Connection status dot on avatar ──────────────────────────────────────────

const statusDot: Record<SyncStatus, { color: string; label: string }> = {
  connected: { color: 'var(--success)', label: 'Connected' },
  connecting: { color: 'var(--warning)', label: 'Reconnecting...' },
  disconnected: { color: 'var(--error)', label: 'Offline — changes saved locally' },
};

function AvatarWithStatus({ src, name, size = 'md' }: { src?: string; name?: string; size?: 'sm' | 'md' | 'lg' | number }) {
  const syncStatus = useSyncStatus();
  const dot = statusDot[syncStatus];

  return (
    <Tooltip title={dot.label} placement="right">
      <Flex
        align="center"
        justify="center"
        className={styles.avatarWrapper}
      >
        <Avatar size={size} src={src}>
          {name?.[0] || '?'}
        </Avatar>
        <Flex
          className={styles.statusDot}
          style={{ '--dot-color': dot.color } as React.CSSProperties}
        />
      </Flex>
    </Tooltip>
  );
}

// ── Profile Button (hover without flicker) ──────────────────────────────────

const ProfileButton = React.forwardRef<HTMLDivElement, { collapsed: boolean; children: React.ReactNode; onClick?: () => void; className?: string }>(
  ({ collapsed, children, onClick, className, ...props }, ref) => {
    return (
      <Flex
        ref={ref}
        align="center"
        justify={collapsed ? 'center' : undefined}
        gap="md"
        onClick={onClick}
        className={clsx(
          styles.profileButtonBase,
          collapsed ? styles.profileButtonCollapsed : styles.profileButtonExpanded,
          className,
        )}
        {...props}
      >
        {children}
      </Flex>
    );
  }
);

// ── Sidebar Seam Toggle ─────────────────────────────────────────────────────
// A full-height, invisible click strip on the sidebar's right edge (the seam
// between the sidebar and the main content). Clicking anywhere along it toggles
// collapse/expand. No visible affordance at rest — only a pointer cursor.

function SidebarSeamToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid="sidebar-seam-toggle"
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={styles.seamToggle}
    />
  );
}

// ── Public component ──────────────────────────────────────────────────────────

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean;
  onSettingsClick?: () => void;
  onSearchClick?: () => void;
  showWindowControls?: boolean;
  onCreateDoc?: (type: 'document' | 'canvas') => void;
  onCreateCollection?: () => void;
  /** Opens the assistant full-tab on a fresh conversation. */
  onNewChat?: () => void;
  /** Opens the billing settings tab named by `tabId` (from the plan upgrade card). */
  onOpenSubscription?: (tabId: string) => void;
}

export function Sidebar({ isOpen, onClose, inline = false, onSettingsClick, onOpenSubscription, onSearchClick, showWindowControls = false, onCreateDoc, onCreateCollection, onNewChat }: SidebarProps) {
  const allDocs = useWorkspaceDocs();
  const allCollections = useWorkspaceCollections();

  const conversations = useConversationList();
  const recentEntities = useMemo(
    () => selectRecentEntities(allDocs, allCollections, conversations, 7),
    [allDocs, allCollections, conversations],
  );

  const location = useLocation();
  const navigateToView = useNavigateToWorkspaceView();
  const { state: sessionState } = useSessionState();

  const openView = (viewId: string) => {
    const viewMap: Record<string, 'home' | 'documents' | 'collections' | 'trash' | 'calendar'> = {
      'view:home': 'home',
      'view:documents': 'documents',
      'view:collections': 'collections',
      'view:trash': 'trash',
      'view:calendar': 'calendar',
    };
    const view = viewMap[viewId];
    if (view) {
      navigateToView(view);
    }
  };

  const { user, logout } = useAuthStore(useShallow((s) => ({
    user: s.user,
    logout: s.logout,
  })));

  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const switchToWorkspace = useWorkspaceStore((s) => s.switchToWorkspace);
  const activeWorkspace = useWorkspaceStore((s) =>
    s.activeWorkspaceId ? s.workspaces.find((w) => w.id === s.activeWorkspaceId) ?? null : null,
  );

  const navigate = useNavigate();

  const sortedWorkspaces = useMemo(() => {
    const personal = workspaces.filter((w) => w.type === 'personal');
    const team = workspaces.filter((w) => w.type === 'team').sort((a, b) => a.name.localeCompare(b.name));
    return [...personal, ...team];
  }, [workspaces]);

  const [createWorkspaceOpen, setCreateWorkspaceOpen] = React.useState(false);

  const bp = useBreakpoint();
  const forceCollapsed = !bp.md;
  const collapsed = forceCollapsed || (!isOpen && inline);
  const shouldReduce = useReducedMotion();

  // Derive active nav item from focused panel's active tab (session-based navigation)
  // Falls back to URL pathname for direct URL visits.
  const activeViewId = (() => {
    const panel = sessionState?.panels[sessionState.focusedPanelId];
    const activeTab = panel?.tabs.find((t) => t.id === panel.activeId);
    if (activeTab?.type === 'view') {
      if (activeTab.id === 'home') return 'view:home';
      if (activeTab.id === 'documents') return 'view:documents';
      if (activeTab.id === 'collections') return 'view:collections';
      if (activeTab.id === 'trash') return 'view:trash';
      if (activeTab.id === 'calendar') return 'view:calendar';
    }
    // URL fallback (for direct URL visits only)
    if (location.pathname === '/') return 'view:home';
    if (location.pathname === '/documents') return 'view:documents';
    if (location.pathname === '/collections') return 'view:collections';
    if (location.pathname === '/trash') return 'view:trash';
    if (location.pathname === '/calendar') return 'view:calendar';
    return null;
  })();

  // ── Profile dropdown menu items ────────────────────────────────────────────
  const profileMenuItems = [
    // Workspace section
    ...sortedWorkspaces.map((ws) => ({
      key: `switch-ws-${ws.id}`,
      label: ws.name,
      icon: (
        <Flex
          align="center"
          justify="center"
          className={styles.wsMenuAvatar}
          style={{ '--avatar-bg': workspaceAvatarColor(ws.id) } as React.CSSProperties}
        >
          <Text className={styles.wsMenuAvatarInitials}>{workspaceInitials(ws.name)}</Text>
        </Flex>
      ),
      selected: ws.id === activeWorkspaceId,
      onClick: () => void switchToWorkspace(ws.id, (path) => navigate({ to: path })),
    })),
    {
      key: 'create-workspace',
      label: 'Create workspace',
      icon: <Icon name="add" size={14} weight={300} />,
      onClick: () => setCreateWorkspaceOpen(true),
    },
    { key: 'dws', label: '', type: 'divider' as const },
    {
      key: 'settings',
      label: 'Settings',
      icon: <Icon name="settings" size={14} weight={300} />,
      onClick: () => onSettingsClick?.(),
    },
    { key: 'd1', label: '', type: 'divider' as const },
    {
      key: 'signout',
      label: 'Sign out',
      icon: <Icon name="logout" size={14} weight={300} />,
      onClick: async () => { await logout(); },
    },
    { key: 'd2', label: '', type: 'divider' as const },
    {
      key: 'help',
      label: 'Help and support',
      icon: <Icon name="help" size={14} weight={300} />,
      onClick: () => {
        openMessenger();
      },
    },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!collapsed) return;
    // Expand when clicking empty space — ignore clicks on interactive elements
    // (buttons, links, dropdowns) so icon actions still work normally.
    const interactive = (e.target as HTMLElement).closest('button, a, [role="button"], [role="menuitem"], [role="menu"]');
    if (!interactive) onClose?.();
  };

  return (
    <nav
      className={nav({ collapsed, smooth: !shouldReduce })}
      onClick={collapsed ? handleNavClick : undefined}
    >
      {/* ── Window controls (macOS — above logo) ────────────────────────────── */}
      {showWindowControls && (
        <Flex
          align="center"
          className={styles.windowControlsRow}
          data-tauri-drag-region
        >
          <WindowControls />
        </Flex>
      )}

      {/* ── Brand row ───────────────────────────────────────────────────────── */}
      {/* AnimatePresence crossfades between the icon mark (collapsed) and the
          full lockup (expanded) so the size change doesn't snap jarringly. */}
      <Flex align="center" justify="center" className={styles.brandRow}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={collapsed ? 'logo-mark' : 'logo-lockup'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className={collapsed ? undefined : styles.brandLockupWrapper}
          >
            <SidebarBrand collapsed={collapsed} />
          </motion.div>
        </AnimatePresence>
      </Flex>


      {/* ── New button ─────────────────────────────────────────────────────── */}
      <Dropdown
        trigger={['click']}
        placement={collapsed ? 'right-start' : 'bottom'}
        matchTriggerWidth={!collapsed}
        menu={{
          items: [
            {
              key: 'new-document',
              label: 'New Document',
              icon: <Icon name={getEntityKindMeta('document').icon} size={14} weight={300} />,
              extra: <Text size="xs" type="secondary">{shortcutLabel('⌘T', 'Ctrl+T')}</Text>,
              onClick: () => onCreateDoc?.('document'),
            },
            {
              key: 'new-canvas',
              label: 'New Canvas',
              icon: <Icon name={getEntityKindMeta('canvas').icon} size={14} weight={300} />,
              extra: <Text size="xs" type="secondary">{shortcutLabel('⌘⇧T', 'Ctrl+Shift+T')}</Text>,
              onClick: () => onCreateDoc?.('canvas'),
            },
            { key: 'd1', label: '', type: 'divider' as const },
            {
              key: 'new-collection',
              label: 'New Collection',
              icon: <Icon name="create_new_folder" size={14} weight={300} />,
              onClick: () => onCreateCollection?.(),
            },
          ],
        }}
      >
        {collapsed ? (
          <Tooltip title={['New', shortcutLabel('⌘T', 'Ctrl+T')].filter(Boolean).join(' · ')} placement="right">
            <IconButton size="md" pressEffect="glow" aria-label="New" className={styles.createIconBtn}>
              <Icon name="add" size={20} weight={400} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="primary"
            pressEffect="glow"
            block
            icon={<Icon name="add" size={16} weight={300} />}
            className={styles.createButton}
          >
            New
          </Button>
        )}
      </Dropdown>

      {/* ── Sidebar seam toggle — invisible click strip on the right edge ── */}
      {!forceCollapsed && <SidebarSeamToggle collapsed={collapsed} onClick={onClose} />}

      {/* ── Nav sections (scrollable) ──────────────────────────────────────── */}
      <Flex
        vertical
        className={styles.navScrollable}
      >
        {collapsed ? (
          /* Collapsed: icon-only nav (navigation only — do NOT toggle sidebar) */
          <Flex vertical align="center" gap="md" className={styles.collapsedNav}>
            <Tooltip title="Home" placement="right">
              <IconButton size="md" aria-label="Home" active={activeViewId === 'view:home'} onClick={() => openView('view:home')}>
                <Icon name="home" size={20} weight={activeViewId === 'view:home' ? 400 : 300} />
              </IconButton>
            </Tooltip>
            <Tooltip title={['Search', shortcutLabel('⌘K', 'Ctrl+K')].filter(Boolean).join(' · ')} placement="right">
              <IconButton size="md" aria-label="Search" onClick={onSearchClick}>
                <Icon name="search" size={20} weight={300} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Chat" placement="right">
              <IconButton size="md" aria-label="Chat" onClick={onNewChat}>
                <Icon name="forum" size={20} weight={300} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Documents" placement="right">
              <IconButton size="md" aria-label="Documents" active={activeViewId === 'view:documents'} onClick={() => openView('view:documents')}>
                <Icon name="auto_stories" size={20} weight={activeViewId === 'view:documents' ? 400 : 300} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collections" placement="right">
              <IconButton size="md" aria-label="Collections" active={activeViewId === 'view:collections'} onClick={() => openView('view:collections')}>
                <Icon name="folder_open" size={20} weight={activeViewId === 'view:collections' ? 400 : 300} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Calendar" placement="right">
              <IconButton size="md" aria-label="Calendar" active={activeViewId === 'view:calendar'} onClick={() => openView('view:calendar')}>
                <Icon name="calendar_month" size={20} weight={activeViewId === 'view:calendar' ? 400 : 300} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Trash" placement="right">
              <IconButton size="md" aria-label="Trash" active={activeViewId === 'view:trash'} onClick={() => openView('view:trash')}>
                <Icon name="delete" size={20} weight={activeViewId === 'view:trash' ? 400 : 300} />
              </IconButton>
            </Tooltip>
          </Flex>
        ) : (
          /* Expanded: Menu component with custom active style */
          <Flex vertical className={styles.expandedNav}>
            <Menu
              mode="inline"
              selectedKeys={activeViewId ? [activeViewId] : []}
              onSelect={(key) => {
                if (key === 'search') onSearchClick?.();
                else if (key === 'new-chat') onNewChat?.();
                else openView(key);
              }}
              className={styles.menuMain}
              items={[
                {
                  key: 'view:home',
                  label: <Text className={styles.menuItemLabelText}>Home</Text>,
                  icon: <Icon name="home" size={18} weight={activeViewId === 'view:home' ? 400 : 300} />,
                },
                {
                  key: 'search',
                  label: <Text className={styles.menuItemLabelText}>Search</Text>,
                  icon: <Icon name="search" size={18} weight={300} />,
                },
                {
                  key: 'new-chat',
                  label: <Text className={styles.menuItemLabelText}>Chat</Text>,
                  icon: <Icon name="forum" size={18} weight={300} />,
                },
                {
                  key: 'view:documents',
                  label: <Text className={styles.menuItemLabelText}>Documents</Text>,
                  icon: <Icon name="auto_stories" size={18} weight={activeViewId === 'view:documents' ? 400 : 300} />,
                },
                {
                  key: 'view:collections',
                  label: <Text className={styles.menuItemLabelText}>Collections</Text>,
                  icon: <Icon name="folder_open" size={18} weight={activeViewId === 'view:collections' ? 400 : 300} />,
                },
                {
                  key: 'view:calendar',
                  label: <Text className={styles.menuItemLabelText}>Calendar</Text>,
                  icon: <Icon name="calendar_month" size={18} weight={activeViewId === 'view:calendar' ? 400 : 300} />,
                },
                {
                  key: 'view:trash',
                  label: <Text className={styles.menuItemLabelText}>Trash</Text>,
                  icon: <Icon name="delete" size={18} weight={activeViewId === 'view:trash' ? 400 : 300} />,
                },
              ]}
            />
            {recentEntities.length > 0 && (
              <>
              <Flex className={styles.recentsDivider} aria-hidden />
              <Collapse
                ghost
                defaultActiveKey="recents"
                className={styles.recentsCollapse}
                items={[{
                  key: 'recents',
                  header: <Text className={styles.recentsHeader}>Recents</Text>,
                  children: (
                    <Flex vertical className={styles.recentsList}>
                      {recentEntities.map((entity) => (
                        <RecentsRow key={entity.id} entity={entity} />
                      ))}
                    </Flex>
                  ),
                }]}
              />
              </>
            )}
          </Flex>
        )}
      </Flex>

      {createWorkspaceOpen && <CreateWorkspaceModal onClose={() => setCreateWorkspaceOpen(false)} />}

      {/* ── Plan indicator (upgrade card for free/trial; chip is in the profile
             row below for paid) ─────────────────────────────────────────────── */}
      <PlanIndicator
        variant="card"
        collapsed={collapsed}
        onUpgrade={() => onOpenSubscription?.(subscriptionTabForWorkspace(activeWorkspace?.type))}
      />

      {/* ── Profile (bottom) ───────────────────────────────────────────────── */}
      <Flex vertical className={profileSection()}>
        <Dropdown
          trigger={['click']}
          placement="top-start"
          menu={{ items: profileMenuItems }}
        >
          {collapsed ? (
            <IconButton size="md" transparent aria-label="Profile menu">
              <AvatarWithStatus src={user?.picture} name={user?.name} />
            </IconButton>
          ) : (
            <ProfileButton collapsed={collapsed}>
              <AvatarWithStatus src={user?.picture} name={user?.name} size={36} />
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={shouldReduce ? { duration: 0 } : undefined}
                  className={styles.profileMotionWrapper}
                >
                  <Flex align="center" gap="xs" className={styles.profileNameRow}>
                    <Text ellipsis className={styles.profileName}>
                      {user?.name || 'User'}
                    </Text>
                    <PlanIndicator variant="chip" onUpgrade={() => onOpenSubscription?.(subscriptionTabForWorkspace(activeWorkspace?.type))} />
                  </Flex>
                  {activeWorkspace && (
                    <Flex align="center" gap="xs" className={styles.profileWorkspaceRow}>
                      <Flex
                        align="center"
                        justify="center"
                        className={styles.profileWorkspaceAvatar}
                        style={{ '--avatar-bg': workspaceAvatarColor(activeWorkspace.id) } as React.CSSProperties}
                      >
                        <Text className={styles.profileWorkspaceAvatarText}>
                          {workspaceInitials(activeWorkspace.name)}
                        </Text>
                      </Flex>
                      <Text ellipsis className={styles.profileWorkspace}>
                        {activeWorkspace.name}
                      </Text>
                    </Flex>
                  )}
                </motion.div>
              </AnimatePresence>
            </ProfileButton>
          )}
        </Dropdown>
      </Flex>
    </nav>
  );
}
